<template>
    <div class="upsell-marketing-domains-container">
        <div
            v-for="domain in uneditableDomains"
            :key="domain.hash"
            class="upsell-marketing-domains-container__uneditable-domain"
        >
            <span class="typographies-paragraph-medium-semibold">{{ domain.sender }}</span>
        </div>
        <ChargebeeUpsellMarketingDomainsItem
            v-for="domain in editableDomains"
            :key="domain.hash"
            :domain="domain"
            @onRemoveDomain="onRemoveDomain(domain)"
            @domainValidated="onDomainValidation"
        />

        <div v-if="shouldDisplayAddDomain" class="upsell-marketing-domains__add-domain">
            <CompassButton
                :label="
                    $t.__(
                        'pages.account.upsell.marketing.ChargebeeUpsellMarketingDomainsManager.add_other_domain',
                    )
                "
                :disabled="getEstimationLoading"
                leftIcon="plus"
                type="tertiary"
                @click="onAddDomain"
            />
            <p>
                <span class="typographies-paragraph-medium-semibold">
                    {{ $filters.numberFormatCurrency(humanizeCentsPrice(unitPriceDomain)) }}
                </span>
                <span class="typographies-paragraph-medium-regular">
                    /{{ recurrencyLabel(recurrence) }}
                </span>
            </p>
        </div>
    </div>
</template>
<script>
import ChargebeeUpsellMarketingDomainsItem from 'vcomponents/pages/account/upsell/marketing/ChargebeeUpsellMarketingDomainsItem.vue';
import generateHash from 'Helpers/hashGenerator.js';
import ChargebeeUpsellMixin from 'mixins/chargebee/ChargebeeUpsellMixins';
import { cloneDeep } from 'lodash';
import { createNamespacedHelpers } from 'vuex';
const chargebeeUpsellStore = createNamespacedHelpers('chargebeeUpsellStore');

export default {
    components: {
        ChargebeeUpsellMarketingDomainsItem,
    },
    mixins: [ChargebeeUpsellMixin],
    props: {
        unitPriceDomain: {
            type: Number,
            required: true,
        },
        recurrence: {
            type: String,
            required: true,
        },
        isMultiDomainsEnabled: {
            type: Boolean,
            required: true,
        },
        isEnabled: {
            type: Boolean,
            required: true,
        },
    },
    computed: {
        ...chargebeeUpsellStore.mapGetters([
            'getDomains',
            'getCustomerData',
            'getChargebeeCatalogue',
            'getPlanSelected',
            'getEstimationLoading',
        ]),
        editableDomains() {
            return this.getDomains.filter((domain) => domain.canBeModified);
        },
        uneditableDomains() {
            return this.getDomains.filter((domain) => !domain.canBeModified);
        },
        shouldDisplayAddDomain() {
            return (
                this.isMultiDomainsEnabled && !this.getDomains.some((domain) => !domain.isValidated)
            );
        },
    },
    watch: {
        isMultiDomainsEnabled() {
            this.$nextTick(() => {
                //We should keep only one domain if the multi domains is disabled
                if (!this.isMultiDomainsEnabled && !!this.getDomains.length) {
                    const domains = cloneDeep(this.getDomains);
                    for (let i = 1; i < domains.length; i++) {
                        const domain = domains[i];
                        if (domain.canBeModified) {
                            this.removeDomain(domain);
                        }
                    }
                    this.updateAddonQuantity({
                        addonId: this.options.pack_marketing_custom_domain.id,
                        quantity: this.getDomains.length,
                        isOption: true,
                    });
                }
            });
        },
        isEnabled: {
            handler(newIsEnabled, oldIsEnabled) {
                if (newIsEnabled && oldIsEnabled !== undefined && !this.getDomains.length) {
                    this.onAddDomain();
                }
            },
            immediate: true,
        },
    },
    created() {
        const customerDomains = this.getCustomerData.meta_data?.domains;
        if (customerDomains && customerDomains.length > 0) {
            for (const customerDomain of customerDomains) {
                this.updateDomain({
                    sender: customerDomain.sender_domain,
                    subDomain: customerDomain.sub_domain,
                    hash: generateHash(),
                    isValidated: true,
                    canBeModified: false,
                });
            }
        }
    },
    methods: {
        ...chargebeeUpsellStore.mapActions(['updateDomain', 'removeDomain', 'updateAddonQuantity']),
         
        onDomainValidation() {
            try {
                this.updateAddonQuantity({
                    addonId: this.options.pack_marketing_custom_domain.id,
                    quantity: this.getDomains.length,
                    isOption: true,
                });
            } catch (e) {
                this.callError(e);
            }
        },
        onAddDomain() {
            this.updateDomain({
                sender: '',
                name: '',
                subDomain: '',
                extension: 'FR',
                hash: generateHash(),
                isValidated: false,
                canBeModified: true,
            });
        },
        onRemoveDomain(domain) {
            this.removeDomain(domain);
            this.$nextTick(() => {
                if (this.getDomains.length === 0) {
                    this.onAddDomain();
                }
                this.updateAddonQuantity({
                    addonId: this.options.pack_marketing_custom_domain.id,
                    quantity: this.getDomains.length,
                    isOption: true,
                });
            });
        },
    },
};
</script>

<style scoped lang="scss">
.upsell-marketing-domains-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3);
    margin-top: var(--spacing-3);

    &__uneditable-domain {
        padding: 14px var(--spacing-3);
        border-radius: 4px;
        border: 1px solid var(--colors-common-border-default);
        background: var(--colors-common-background-subtle);
    }
}
.upsell-marketing-domains__add-domain {
    display: flex;
    align-items: center;
    gap: var(--spacing-1);
    p {
        margin: 0;
    }
}
</style>
